/**
 * SearchLatencyBadge — Admin-only diagnostic pill rendered next to the
 * Orders Management search input. Surfaces query duration + the backend
 * execution path so the Ops team can validate the search-index rollout
 * in real time (e.g. confirm `digit_id_anchored` flips from COLLSCAN to
 * IXSCAN once the proposed indexes ship to production).
 *
 * Visibility contract:
 *   • Strictly gated on `nRole(user.role) === 'admin'` — hidden for
 *     every other role.
 *   • Hidden when there is no meta yet, no search has been performed,
 *     or the backend reports `path === 'no_search'`.
 *   • While a fresh request is in-flight (`isSearching`) we keep the
 *     last good value visible (slightly dimmed) so the pill doesn't
 *     flicker between keystrokes.
 *
 * Format: `Search: {ms}ms • {Friendly Path}`
 */

import React from 'react';

const ROLE_ALIASES = {
  'Admin': 'admin',
  'Super Admin': 'admin',
  'admin': 'admin',
  'super_admin': 'admin',
};

const nRole = (role) => ROLE_ALIASES[role] || role;

// Friendly labels for each backend `search_meta.path` value. The
// backend currently emits: no_search | state_code | digit_id_anchored
// | regex_scan | exact_id_fast.
const PATH_LABEL = {
  exact_id_fast: 'Fast path',
  digit_id_anchored: 'IXSCAN',
  regex_scan: 'COLLSCAN',
  state_code: 'State scan',
  no_search: '—',
};

// Color the pill by performance class so admins get a quick visual
// signal at a glance — green = healthy fast path, amber = scanning,
// blue = no search yet.
const PATH_CLASSES = {
  exact_id_fast:
    'bg-emerald-50 text-emerald-700 border-emerald-200',
  digit_id_anchored:
    'bg-emerald-50 text-emerald-700 border-emerald-200',
  state_code:
    'bg-blue-50 text-blue-700 border-blue-200',
  regex_scan:
    'bg-amber-50 text-amber-700 border-amber-200',
  no_search:
    'bg-gray-50 text-gray-600 border-gray-200',
};

export const SearchLatencyBadge = ({ user, meta, isSearching }) => {
  if (nRole(user?.role) !== 'admin') return null;
  if (!meta || !meta.has_search) return null;

  const ms = Number.isFinite(meta.duration_ms) ? meta.duration_ms : 0;
  const path = meta.path || 'regex_scan';
  const label = PATH_LABEL[path] || path;
  const tone = PATH_CLASSES[path] || PATH_CLASSES.regex_scan;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium whitespace-nowrap transition-opacity ${tone}`}
      style={{ opacity: isSearching ? 0.55 : 1 }}
      title={`Backend execution path: ${path} • ${meta.result_count ?? 0} result(s) • ${meta.fast_path ? 'fast-path hit' : 'standard query'}`}
      data-testid="orders-search-latency-badge"
    >
      <span className="text-[10px] uppercase tracking-wide text-current opacity-70">
        Search
      </span>
      <span className="font-semibold tabular-nums">{ms}ms</span>
      <span aria-hidden="true" className="opacity-50">•</span>
      <span>{label}</span>
    </div>
  );
};

export default SearchLatencyBadge;
